import React, {
  forwardRef,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import {
  Header as MantineHeader,
  MediaQuery,
  Burger,
  useMantineTheme,
  Avatar,
  Menu,
  Group,
  ActionIcon,
  Text,
  Select,
  SelectItem,
} from "@mantine/core";
import { useStyles } from "./styles";
import { AppContext } from "src/api/AppContext";
import { useLocation, useNavigate } from "react-router-dom";
import { BsDot, BsMoonStarsFill, BsSunFill } from "react-icons/bs";
import { BiSolidShoppingBagAlt, BiUserCircle } from "react-icons/bi";
import { ApolloClientContext } from "src/api/ApolloClientContext";
import { useModals } from "@mantine/modals";
import { errorNotification } from "src/utils/alerts";
import Clock from "../Clock";
import { RolePermissionType, UserRole } from "src/generated/graphql";
import { AiFillShopping } from "react-icons/ai";
import ProductCatalogListing from "src/modules/ProductCatalog/ProductCatalogListing";
import { CatalogModalContext } from "src/api/context/CatalogModalContext";
import CheckInQueue from "src/modules/CheckInQueue";
import { TbChecklist } from "react-icons/tb";
import { ModalId, fullScreenModalCss } from "src/utils/constants";

interface HeaderProps {
  setNavbarOpened: React.Dispatch<React.SetStateAction<boolean>>;
  isNavbarOpen: boolean;
  showLocationSelect: boolean;
  showUserMenu: boolean;
  showThemeIcon?: boolean;
  hideNavMenu?: boolean;
}

const Header = ({
  setNavbarOpened,
  isNavbarOpen,
  showLocationSelect,
  showUserMenu,
  showThemeIcon,
  hideNavMenu,
}: HeaderProps) => {
  const { isOpenCatalog, handleCatalogModal, handleCloseCatalogModal } =
    useContext(CatalogModalContext);
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { classes } = useStyles();
  const {
    currentUser,
    isDarkMode,
    setIsDarkMode,
    currentPageTitle,
    currentLocation,
    setCurrentLocation,
    currentOrganization,
    currentEmployee,
    setselectedCheckInId,
  } = useContext(AppContext);
  const [isOpenCheckInQueue, setisOpenCheckInQueue] = useState(false);
  const modals = useModals();
  const location = useLocation();
  const { logout } = useContext(ApolloClientContext);

  const [showCheckIn, setshowCheckIn] = useState(true);
  const [showCatalog, setshowCatalog] = useState(true);
  const [showClockIn, setshowClockIn] = useState(true);

  useEffect(() => {
    if (currentEmployee?.role) {
      const hasPermissionCheckIn =
        currentEmployee.role?.checkInQueue >= RolePermissionType.Read;
      setshowCheckIn(hasPermissionCheckIn);

      const hasPermissionCatalog =
        currentEmployee.role?.catalog >= RolePermissionType.Read;
      setshowCatalog(hasPermissionCatalog);

      const hasPermissionClock =
        currentEmployee.role?.personalTimeClock === RolePermissionType.Write;
      setshowClockIn(hasPermissionClock);
    } else {
      setshowCheckIn(true);
      setshowCatalog(true);
      setshowClockIn(true);
    }
  }, [currentEmployee]);

  useEffect(() => {
    // location change close modal if opened
    handleCloseCheckInModal();
    handleCloseCatalogModal && handleCloseCatalogModal();
  }, [location]);

  const handleCloseCheckInModal = () => {
    if (isOpenCheckInQueue) {
      setisOpenCheckInQueue(false);
      modals.closeModal(ModalId.CheckInModalId);
    }
  };

  const getData = () => {
    return ((currentEmployee
      ? currentEmployee?.locations
      : currentOrganization?.locations
    )?.map((loc) => ({
      image: loc.logoUrl,
      label: loc.name,
      value: loc.id,
      description: loc.description || "",
    })) || []) as SelectItem[];
  };

  interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
    image: string;
    label: string;
    description: string;
  }

  const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
    ({ image, label, description, ...others }: ItemProps, ref) => (
      <div ref={ref} {...others}>
        <Group noWrap>
          <Avatar src={image} />

          <div>
            <Text size="sm">{label}</Text>
            <Text size="xs" opacity={0.65}>
              {description}
            </Text>
          </div>
        </Group>
      </div>
    )
  );

  const handleClock = useCallback(() => {
    if (!currentLocation?.id)
      return errorNotification({ message: "Please Select active location" });
    modals.openModal({
      closeOnClickOutside: false,
      title: `Confirm the Clock-${currentEmployee?.clockedIn ? "Out" : "In"}`,
      children: (
        <Clock
          stationsEnabled={!!currentLocation?.stationsEnabled}
          isClockedIn={!!currentEmployee?.clockedIn}
        />
      ),
    });
  }, [currentLocation, currentEmployee?.clockedIn]);

  const handleModalCatalog = useCallback(() => {
    handleCloseCheckInModal();
    handleCatalogModal();
    !isOpenCatalog &&
      modals.openModal({
        modalId: ModalId.CatalogModalId,
        closeOnClickOutside: false,
        fullScreen: true,
        children: <ProductCatalogListing />,
        styles: {
          ...fullScreenModalCss,
        },
        sx: {
          ".mantine-Grid-root": {
            height: "100%  !important",
          },
          ".mantine-Modal-body": {
            height:
              "calc(100% - (var(--mantine-footer-height, 0px)) ) !important",
            overflow: "auto",
          },
        },
      });
  }, [isOpenCatalog, modals]);

  const handleModalCheckInQueue = useCallback(() => {
    setselectedCheckInId("");
    handleCloseCatalogModal();
    setisOpenCheckInQueue(true);
    modals.openModal({
      modalId: ModalId.CheckInModalId,
      fullScreen: true,
      children: <CheckInQueue />,
      styles: {
        ...fullScreenModalCss,
      },
      sx: {
        ".mantine-Modal-body": {
          backgroundColor: theme.colors.secondary[2],
        },
        ".mantine-Grid-root": {
          height:
            "calc(100vh - (var(--mantine-header-height, 0px) + var(--mantine-footer-height, 0px)) ) !important",
          overflow: "auto",
        },
      },
    });
  }, [modals]);

  return (
    <MantineHeader
      height={58}
      p="sm"
      style={{
        backgroundColor: theme.colors.secondary[6],
        borderBottom: "none",
        color: theme.colors.secondaryTextColor[6],
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Group position="left" spacing="xs">
          {!hideNavMenu && (
            <MediaQuery largerThan="md" styles={{ display: "none" }}>
              <Burger
                color={theme.colors.secondaryTextColor[6]}
                onClick={() => setNavbarOpened((isOpen) => !isOpen)}
                opened={isNavbarOpen}
                size="md"
              />
            </MediaQuery>
          )}
          <Group position="left" spacing={0}>
            <Text
              size={18}
              weight={500}
              color={theme.colors.secondaryTextColor[6]}
              onClick={() => navigate("/")}
              sx={{
                paddingLeft: 8,
                paddingRight: 8,
                cursor: "pointer",
                "&:hover": {
                  borderRadius: 20,
                  backgroundColor: theme.colors.secondary[0],
                },
              }}
            >
              {currentOrganization?.name}
            </Text>
            {currentPageTitle && (
              <BsDot color={theme.colors.secondaryTextColor[6]} />
            )}
            <Text
              size={18}
              weight={500}
              color={theme.colors.secondaryTextColor[6]}
              sx={{
                paddingLeft: 8,
                paddingRight: 8,
              }}
            >
              {currentPageTitle}
            </Text>
          </Group>
        </Group>
        <Group position="right" style={{ flex: 1, height: "100%" }}>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              height: "100%",
              minWidth: 200,
              justifyContent: "flex-end",
              paddingRight: 10,
            }}
          >
            {showLocationSelect && (
              <Select
                //label="Choose employee of the month"
                placeholder="Active Location"
                itemComponent={SelectItem}
                data={getData()}
                searchable
                maxDropdownHeight={400}
                nothingFound="Nobody here"
                value={currentLocation?.id || undefined}
                filter={(value, item) =>
                  item.label
                    ?.toLowerCase()
                    .includes(value.toLowerCase().trim()) ||
                  (item.description &&
                    item.description
                      .toLowerCase()
                      .includes(value.toLowerCase().trim()))
                }
                onChange={(val) => {
                  let target = (
                    currentEmployee
                      ? currentEmployee?.locations
                      : currentOrganization?.locations
                  )?.find((loc) => loc.id === val);
                  if (target) {
                    setCurrentLocation(target);
                  }
                }}
                style={{
                  marginRight: 15,
                }}
                sx={{
                  input: {
                    borderRadius: 20,
                    minHeight: 30,
                    height: 30,
                  },
                  ".mantine-Input-input": {
                    color: `${theme.colors.secondaryTextColor[4]} !important`,
                    backgroundColor: theme.colors.secondary[4],
                  },
                  ".mantine-Select-dropdown": {
                    color: theme.colors.secondaryTextColor[4],
                    backgroundColor: theme.colors.secondary[4],
                    borderColor: theme.colors.secondary[0],
                    ".mantine-Select-item": {
                      backgroundColor: theme.colors.secondary[4],
                      color: theme.colors.secondaryTextColor[4],
                      "&:hover": {
                        backgroundColor: theme.colors.secondary[2],
                      },
                    },
                  },
                }}
              />
            )}
            {showThemeIcon && false && (
              <ActionIcon
                onClick={() => setIsDarkMode(!isDarkMode)}
                style={{
                  marginRight: 16,
                  padding: 6,
                  height: 36,
                  width: 36,
                }}
                sx={{
                  backgroundColor: theme.colors.primary[6],
                  "&:hover": {
                    backgroundColor: theme.colors.primary[4],
                  },
                }}
                className={classes.headerItem}
                radius="xl"
              >
                {isDarkMode ? (
                  <BsSunFill
                    size={28}
                    color={theme.colors.primaryTextColor[6]}
                  />
                ) : (
                  <BsMoonStarsFill
                    size={28}
                    color={theme.colors.primaryTextColor[6]}
                  />
                )}
              </ActionIcon>
            )}

            {showUserMenu && (
              <>
                {showCatalog && (
                  <ActionIcon
                    onClick={handleModalCatalog}
                    style={{
                      marginRight: 16,
                      padding: 4,
                      height: 36,
                      width: 36,
                    }}
                    sx={{
                      backgroundColor: theme.colors.primary[6],
                      "&:hover": {
                        backgroundColor: theme.colors.primary[4],
                      },
                    }}
                    className={classes.headerItem}
                    radius="xl"
                  >
                    {isOpenCatalog ? (
                      <BiSolidShoppingBagAlt
                        size={28}
                        color={theme.colors.primaryTextColor[6]}
                      />
                    ) : (
                      <AiFillShopping
                        size={28}
                        color={theme.colors.primaryTextColor[6]}
                      />
                    )}
                  </ActionIcon>
                )}
                {showCheckIn && (
                  <ActionIcon
                    onClick={() =>
                      isOpenCheckInQueue
                        ? handleCloseCheckInModal()
                        : handleModalCheckInQueue()
                    }
                    style={{
                      marginRight: 16,
                      padding: 4,
                      height: 36,
                      width: 36,
                    }}
                    sx={{
                      backgroundColor: theme.colors.primary[6],
                      "&:hover": {
                        backgroundColor: theme.colors.primary[4],
                      },
                    }}
                    className={classes.headerItem}
                    radius="xl"
                  >
                    <TbChecklist
                      size={28}
                      color={theme.colors.primaryTextColor[6]}
                    />
                  </ActionIcon>
                )}
              </>
            )}

            {showUserMenu && (
              <Menu>
                <Menu.Target>
                  <ActionIcon
                    className={classes.headerItem}
                    radius="xl"
                    style={{
                      padding: 4,
                      height: 36,
                      width: 36,
                    }}
                    sx={{
                      backgroundColor: theme.colors.primary[6],
                      "&:hover": {
                        backgroundColor: theme.colors.primary[4],
                      },
                    }}
                  >
                    {currentUser?.profilePicUrl ? (
                      <Avatar
                        radius="xl"
                        sx={{
                          ":root": {
                            justifyContent: "center",
                            alignItems: "center",
                            display: "flex",
                          },
                        }}
                        size="sm"
                        src={currentUser.profilePicUrl}
                      />
                    ) : (
                      <BiUserCircle
                        size={28}
                        color={theme.colors.primaryTextColor[6]}
                      />
                    )}
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{"Account"}</Menu.Label>
                  <Menu.Item onClick={() => navigate("/profile")}>
                    {"My Profile"}
                  </Menu.Item>
                  {currentUser?.role === UserRole.Employee &&
                    showClockIn &&
                    currentLocation?.stationsEnabled && (
                      <Menu.Item onClick={handleClock}>
                        {`Clock-${currentEmployee?.clockedIn ? "Out" : "In"}`}
                      </Menu.Item>
                    )}
                  <Menu.Item color="red" onClick={logout}>
                    {"Sign out"}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </div>
        </Group>
      </div>
    </MantineHeader>
  );
};

export default Header;
